import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Divider,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Zoom,
  CardHeader,
  LinearProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  Stars as StarsIcon,
  Lock as LockIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Verified as VerifiedIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  LocalOffer as LocalOfferIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import authService from '../services/authService';

// Helper function to convert English city names back to Arabic for display
const mapCityToArabic = (cityName) => {
  if (!cityName) return 'غير محدد';
  
  const cityToArabicMapping = {
    'Baghdad': 'بغداد',
    'Basra': 'البصرة', 
    'Erbil': 'أربيل',
    'Najaf': 'النجف',
    'Karbala': 'كربلاء',
    'Sulaymaniyah': 'السليمانية',
    'Mosul': 'الموصل',
    'Duhok': 'دهوك',
    'Babylon': 'بابل',
    'Kirkuk': 'كركوك',
    'DhiQar': 'ذي قار',
    'Muthanna': 'المثنى',
    'Qadisiyyah': 'القادسية',
    'Maysan': 'ميسان',
    'Saladin': 'صلاح الدين',
    'Diyala': 'ديالى',
    'Anbar': 'الأنبار',
    'Wasit': 'واسط'
  };
  
  return cityToArabicMapping[cityName] || cityName;
};

const Profile = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // User statistics state
  const [userStats, setUserStats] = useState({
    totalOrders: 0,
    totalPurchases: 0,
    points: 0,
    pendingOrders: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Password reset state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const currentUser = authService.getCurrentUser();
    const storedUser = localStorage.getItem('user');
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setEditData(parsedUser);
        setLoading(false);
        
        // Update stats from cached user data
        setUserStats({
          totalOrders: parsedUser.total_orders || 0,
          pendingOrders: Math.floor((parsedUser.total_orders || 0) * 0.15),
          totalPurchases: parsedUser.total_spent || 0,
          points: parsedUser.points || 0
        });
        setStatsLoading(false);
        
        fetchUserProfile();
      } catch (err) {
        localStorage.removeItem('user');
        navigate('/login');
      }
    } else {
      fetchUserProfile();
    }
  }, [navigate]);

  const fetchUserProfile = async () => {
    try {
      if (!authService.isAuthenticated()) {
        navigate('/login');
        return;
      }

      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/profile.php`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('فشل في تحميل بيانات الملف الشخصي');
      }

      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        setEditData(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        setError('');
        
        // Update user statistics from profile data
        setUserStats({
          totalOrders: data.user.total_orders || 0,
          pendingOrders: Math.floor((data.user.total_orders || 0) * 0.15), // Estimate 15% as pending
          totalPurchases: data.user.total_spent || 0,
          points: data.user.points || 0
        });
        setStatsLoading(false);
      } else {
        setError(data.error || 'حدث خطأ في تحميل البيانات');
      }
    } catch (err) {
      if (err.message.includes('Authentication expired')) {
        navigate('/login');
      } else {
        setError('تعذر الاتصال بالخادم - يتم عرض البيانات المحفوظة');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      
      // Fetch user orders and statistics
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/user_orders.php`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.statistics) {
          setUserStats({
            totalOrders: data.statistics.totalOrders || 0,
            pendingOrders: data.statistics.pendingOrders || 0,
            totalPurchases: data.statistics.totalPurchases || 0,
            points: data.statistics.points || 0
          });
        } else {
          // Use mock data if API fails
          setUserStats({
            totalOrders: 5,
            pendingOrders: 1,
            totalPurchases: 450000,
            points: 75
          });
        }
      } else {
        // Use mock data if API fails
        setUserStats({
          totalOrders: 5,
          pendingOrders: 1,
          totalPurchases: 450000,
          points: 75
        });
      }

    } catch (err) {
      console.error('Error fetching user stats:', err);
      // Set mock data on error
      setUserStats({
        totalOrders: 5,
        pendingOrders: 1,
        totalPurchases: 450000,
        points: 75
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    setSaving(true);
    
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/profile.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: editData.name,
          email: editData.email,
          phone: editData.phone,
          address: editData.address,
          city: editData.city
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        setEditing(false);
        setSnackbarMessage('تم حفظ التغييرات بنجاح');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(data.error || 'فشل في حفظ التغييرات');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('خطأ في الاتصال بالخادم');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setSnackbarMessage('جميع الحقول مطلوبة');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbarMessage('كلمة المرور الجديدة غير متطابقة');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSnackbarMessage('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/profile.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'change_password',
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSnackbarMessage('تم تغيير كلمة المرور بنجاح');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handlePasswordDialogClose();
      } else {
        setSnackbarMessage(data.error || 'فشل في تغيير كلمة المرور');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('خطأ في الاتصال بالخادم');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
  };

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordVisibilityToggle = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card sx={{ 
          p: 4, 
          borderRadius: 4, 
          background: '#ffffff',
          border: '1px solid #e9ecef',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography>جاري تحميل بيانات الملف الشخصي...</Typography>
          </Stack>
        </Card>
      </Box>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: '#f8f9fa',
      pt: 12,
      pb: 4
    }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            {/* Modern Profile Header */}
            <Card 
              elevation={0}
              sx={{ 
                mb: 4, 
                borderRadius: 4,
                background: '#2c3e50',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(44, 62, 80, 0.15)'
              }}
            >
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                <Slide direction="down" in timeout={1000}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Zoom in timeout={1200}>
                      <Avatar 
                        sx={{ 
                          width: 80, 
                          height: 80,
                          background: '#3B82F6',
                          fontSize: '2rem'
                        }}
                      >
                        <PersonIcon fontSize="large" />
                      </Avatar>
                    </Zoom>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {user?.name || 'المستخدم'}
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        {user?.email}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Chip 
                          label="مفعل" 
                          size="small"
                          sx={{ 
                            background: '#3B82F6',
                            color: 'white',
                          }}
                        />
                        <Chip 
                          label={user?.user_type || 'عميل'} 
                          size="small"
                          sx={{ 
                            background: '#3B82F6',
                            color: 'white',
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Slide>
              </CardContent>
            </Card>

            {/* Profile Information Cards */}
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Slide direction="up" in timeout={1000}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      borderRadius: 3,
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                    }}
                  >
                    <CardHeader
                      avatar={
                        <Avatar sx={{ background: '#34495e', color: 'white' }}>
                          <PersonIcon />
                        </Avatar>
                      }
                      title="المعلومات الشخصية"
                      titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                      sx={{ pb: 1 }}
                    />
                    <Divider />
                    <CardContent sx={{ p: 3 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="الاسم"
                            value={editing ? editData.name || '' : user?.name || ''}
                            onChange={(e) => setEditData({...editData, name: e.target.value})}
                            disabled={!editing}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon color="primary" />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="البريد الإلكتروني"
                            value={editing ? editData.email || '' : user?.email || ''}
                            onChange={(e) => setEditData({...editData, email: e.target.value})}
                            disabled={!editing}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon color="primary" />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="رقم الهاتف"
                            value={editing ? editData.phone || '' : user?.phone || ''}
                            onChange={(e) => setEditData({...editData, phone: e.target.value})}
                            disabled={!editing}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon color="primary" />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth disabled={!editing}>
                            <InputLabel>المحافظة</InputLabel>
                            <Select
                              value={editing ? editData.city || '' : user?.city || ''}
                              onChange={(e) => setEditData({...editData, city: e.target.value})}
                              label="المحافظة"
                              startAdornment={
                                <InputAdornment position="start">
                                  <LocationIcon color="primary" />
                                </InputAdornment>
                              }
                            >
                              <MenuItem value="Baghdad">بغداد</MenuItem>
                              <MenuItem value="Basra">البصرة</MenuItem>
                              <MenuItem value="Erbil">أربيل</MenuItem>
                              <MenuItem value="Najaf">النجف</MenuItem>
                              <MenuItem value="Karbala">كربلاء</MenuItem>
                              <MenuItem value="Sulaymaniyah">السليمانية</MenuItem>
                              <MenuItem value="Mosul">الموصل</MenuItem>
                              <MenuItem value="Duhok">دهوك</MenuItem>
                              <MenuItem value="Babylon">بابل</MenuItem>
                              <MenuItem value="Kirkuk">كركوك</MenuItem>
                              <MenuItem value="DhiQar">ذي قار</MenuItem>
                              <MenuItem value="Muthanna">المثنى</MenuItem>
                              <MenuItem value="Qadisiyyah">القادسية</MenuItem>
                              <MenuItem value="Maysan">ميسان</MenuItem>
                              <MenuItem value="Saladin">صلاح الدين</MenuItem>
                              <MenuItem value="Diyala">ديالى</MenuItem>
                              <MenuItem value="Anbar">الأنبار</MenuItem>
                              <MenuItem value="Wasit">واسط</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="العنوان"
                            value={editing ? editData.address || '' : user?.address || ''}
                            onChange={(e) => setEditData({...editData, address: e.target.value})}
                            disabled={!editing}
                            multiline
                            rows={2}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon color="primary" />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      {editing && (
                        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setEditing(false);
                              setEditData(user);
                            }}
                            startIcon={<CancelIcon />}
                            size="large"
                            sx={{
                              color: '#ffffffff',
                              borderColor: '#fafafaff',
                              borderWidth: 2,
                              borderRadius: 2,
                              py: 1,
                              px: 4,
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '1rem',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: '#ffffffff',
                                borderColor: '#ffffffff',
                                color: 'white',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(255, 255, 255, 1)'
                              }
                            }}
                          >
                            إلغاء
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                            size="large"
                            sx={{
                              backgroundColor: '#52c41a',
                              color: 'white',
                              borderRadius: 2,
                              py: 1,
                              px: 4,
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '1rem',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: '#73d13d',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                              },
                              '&:disabled': {
                                backgroundColor: '#d9d9d9',
                                color: '#bfbfbf',
                                transform: 'none',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>

              {/* Action Buttons and Statistics Section */}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => setEditing(!editing)}
                        startIcon={editing ? <CloseIcon /> : <EditIcon />}
                        sx={{
                          backgroundColor: '#2c3e50',
                          color: 'white',
                          borderRadius: '25px',
                          py: 1.5,
                          px: 4,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '1rem',
                          minWidth: 160,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#34495e',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(44, 62, 80, 0.3)'
                          }
                        }}
                      >
                        {editing ? 'إلغاء التعديل' : 'تعديل الملف الشخصي'}
                      </Button>
                      
                      <Button
                        variant="contained"
                        onClick={handlePasswordDialogOpen}
                        startIcon={<LockResetIcon />}
                        sx={{
                          backgroundColor: '#2c3e50',
                          color: 'white',
                          borderRadius: '25px',
                          py: 1.5,
                          px: 4,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '1rem',
                          minWidth: 160,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#34495e',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(44, 62, 80, 0.3)'
                          }
                        }}
                      >
                        تغيير كلمة المرور
                      </Button>
                    </Box>
                  </Grid>

                  {/* Statistics Cards */}
                  <Grid item xs={4} md={4}>
                    {/* Orders Statistics */}
                    <Zoom in timeout={1200}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          borderRadius: 3,
                          background: '#3498db',
                          color: 'white',
                          cursor: 'pointer',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          boxShadow: '0 4px 15px rgba(52, 152, 219, 0.2)',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.3)'
                          }
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <ShoppingCartIcon sx={{ fontSize: 40, mb: 2 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {statsLoading ? <CircularProgress size={24} color="inherit" /> : userStats.totalOrders}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            إجمالي الطلبات
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {statsLoading ? '...' : `${userStats.pendingOrders} طلب قيد المعالجة`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>

                  <Grid item xs={4} md={4}>
                    {/* Total Purchases */}
                    <Zoom in timeout={1400}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          borderRadius: 3,
                          background: '#27ae60',
                          color: 'white',
                          cursor: 'pointer',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          boxShadow: '0 4px 15px rgba(39, 174, 96, 0.2)',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 25px rgba(39, 174, 96, 0.3)'
                          }
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <AttachMoneyIcon sx={{ fontSize: 40, mb: 2 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {statsLoading ? <CircularProgress size={24} color="inherit" /> : `${userStats.totalPurchases.toLocaleString()}`}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            إجمالي المشتريات
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            دينار عراقي
                          </Typography>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>

                  {/* Points - Hidden for wholesale users */}
                  {(() => {
                    // Get user role from authService (same as Header component)
                    const currentUser = authService.getCurrentUser();
                    const userRole = currentUser?.role || currentUser?.user_role || '';
                    const isWholesaleUser = userRole === 'whole';
                    
                    return !isWholesaleUser;
                  })() && (
                    <Grid item xs={4} md={4}>
                      <Zoom in timeout={1600}>
                        <Card 
                          elevation={0}
                          sx={{ 
                            borderRadius: 3,
                            background: '#f39c12',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            boxShadow: '0 4px 15px rgba(243, 156, 18, 0.2)',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 8px 25px rgba(243, 156, 18, 0.3)'
                            }
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <StarsIcon sx={{ fontSize: 40, mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {statsLoading ? <CircularProgress size={24} color="inherit" /> : userStats.points}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                              نقاط المكافآت
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              يمكن استخدامها 
                            </Typography>
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>

      {/* Password Reset Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#ffffff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: '#34495e',
          color: 'white',
          m: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <SecurityIcon />
          تغيير كلمة المرور
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="كلمة المرور الحالية"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handlePasswordVisibilityToggle('current')}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="كلمة المرور الجديدة"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              variant="outlined"
              required
              helperText="يجب أن تكون 6 أحرف على الأقل"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handlePasswordVisibilityToggle('new')}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="تأكيد كلمة المرور الجديدة"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              variant="outlined"
              required
              error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
              helperText={
                passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                  ? 'كلمة المرور غير متطابقة'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handlePasswordVisibilityToggle('confirm')}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
          <Button 
            onClick={handlePasswordDialogClose}
            variant="outlined"
            disabled={passwordLoading}
            size="large"
            sx={{
              color: '#ffffffff',
              borderColor: '#ffffffff',
              borderWidth: 2,
              borderRadius: 2,
              py: 1,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#ffffffff',
                borderColor: '#ffffffff',
                color: 'white',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 1)'
              }
            }}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handlePasswordReset}
            variant="contained"
            disabled={passwordLoading}
            startIcon={passwordLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            size="large"
            sx={{
              backgroundColor: '#3b5dac',
              color: 'white',
              borderRadius: 2,
              py: 1,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#2a406e',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              },
              '&:disabled': {
                backgroundColor: '#d9d9d9',
                color: '#bfbfbf',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {passwordLoading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
